import { LandingPageContent } from '../types';

/**
 * Language page for the Greek market and — at least as importantly — the
 * Greek diaspora (US, UK, Australia, Germany), who search for these terms in
 * English. Converts the product's Greek-language capability into an SEO asset
 * without needing localized URL infrastructure.
 */
const greekBooksForKids: LandingPageContent = {
	path: '/personalized-greek-books-for-kids',
	seoTitle: 'Personalized Greek Books for Kids — Your Child as the Hero, in Greek',
	metaDescription:
		'Personalized children’s books in Greek: your child illustrated as the hero, with the story written in Greek. Made for Greek families and the diaspora — bilingual households welcome.',
	heading: 'Personalized Greek Books for Kids',
	subheading: 'Their face in the pictures. Their language on the page.',
	heroImage: '/assets/hero/kid-5/watercolor_scene.png',
	breadcrumbParents: [{ name: 'Personalized story books', url: '/personalized-story-books' }],
	intro: `Finding good Greek children's books outside Greece is harder than it should be. Finding one where your own child is the illustrated hero — that, until recently, wasn't possible at all.

That is what this is: you upload a photo, we illustrate your child into a storybook, and the story is written in Greek. Not translated as an afterthought — produced in Greek, for families who live in the language or are trying to keep a child connected to it.

For diaspora families especially, this solves a real problem: the child who answers in English no matter what language the question came in.`,
	sections: [
		{
			heading: 'Why a personalized book works for a heritage language',
			body: `Children resist a second language for a simple reason: the interesting things happen in the first one. Greek is the language of obligation — Sunday school, phone calls with relatives — while cartoons and playgrounds run in English.

A book where the child is the hero flips that incentive. They want to know what *they* do on the next page, and the answer only exists in Greek. The motivation problem — the entire battle of heritage-language parenting — is bypassed, because no child is indifferent to a story about themselves.

It also gives the Greek-speaking adults a role. Yiayia doesn't just read the book; she is the only one who can unlock it properly. That is not a small thing across a video call.`,
		},
		{
			heading: 'Who orders these',
			body: `**Families in Greece and Cyprus** wanting a personalized book that isn't in English by default.

**Diaspora parents** — in Melbourne, Chicago, London, Munich — raising bilingual children and losing the numbers game against English.

**Grandparents and godparents** who want to give something in Greek that the child will genuinely use. A vaptisi or name-day gift in the family language, starring the child, is about as targeted as gifting gets. See our [christening gifts guide](/gifts/christening-gifts).

**Mixed-language households**, where one parent reads in Greek and the other in English — a personalized story is often ordered in both.`,
		},
		{
			heading: 'The details that matter in Greek',
			body: `**The name is rendered properly.** Greek names carry accents and inflect; a story about Νίκος needs to say του Νίκου where grammar demands it. The name isn't just pasted in — the story accommodates it.

**Real Greek, not machine output pasted into a template.** Stories are curated per language rather than run through a translator, because children's Greek has rhythms — the repetitions, the diminutives — that literal translation flattens.

**The likeness carries the book.** For a child who can't read Greek yet, the pictures do the persuading. Because the character is unmistakably them, they follow the story visually while the language arrives read-aloud — which is exactly how first languages are learned too.`,
		},
		{
			heading: 'Occasions where a Greek book lands best',
			body: `- **Name days.** In Greece the name day rivals the birthday, and a book threaded with that very name is almost suspiciously on-theme. More in our [name day gift guide](/blog/name-day-gift-ideas).
- **Vaptisi.** The keepsake brief, met in the family language.
- **Christmas and Easter.** The big diaspora gatherings, where a Greek book from the grandparents does double duty as gift and gentle agenda.
- **Departures.** Families moving abroad, or children of Greek parents growing up away — a book in Greek, starring them, is a piece of the language they own personally.`,
		},
	],
	picks: [
		{
			label: 'Personalized storybooks',
			href: '/personalized-story-books',
			description: 'The full storybook range — choose Greek as the story language.',
		},
		{
			label: 'Personalized coloring books',
			href: '/personalized-coloring-books',
			description: 'Language-light, likeness-heavy — good alongside a Greek storybook.',
		},
	],
	faqs: [
		{
			question: 'Can I get a personalized children’s book in Greek?',
			answer:
				'Yes. The story is produced in Greek — with the child’s name handled correctly, accents and grammar included — and your child appears as the illustrated hero throughout. You approve every page before printing.',
		},
		{
			question: 'Are personalized Greek books good for bilingual children?',
			answer:
				'They are one of the more effective tools available. A story starring the child gives them a personal reason to engage with Greek, which is precisely what most bilingual children lack. Many families order the same story in Greek and English.',
		},
		{
			question: 'Do you ship Greek books outside Greece?',
			answer:
				'Yes — diaspora families are a large share of these orders. Books are printed and shipped internationally, including to the US, UK, Australia and across the EU.',
		},
		{
			question: 'Is the Greek machine-translated?',
			answer:
				'No. Stories are curated per language rather than machine-translated, because children’s Greek has rhythms and diminutives that literal translation loses.',
		},
	],
	related: [
		{ label: 'Personalized story books', href: '/personalized-story-books' },
		{ label: 'Christening gifts', href: '/gifts/christening-gifts' },
		{ label: 'Personalized Arabic books for kids', href: '/personalized-arabic-books-for-kids' },
		{ label: 'Why personalized books help kids read', href: '/blog/why-personalized-books-help-kids-read' },
	],
	cta: { label: 'Make a book in Greek', href: '/products' },
};

export default greekBooksForKids;
